import type de from './de';

/** UI texts of the web client (English; also the fallback for fr/nl). */
const en: typeof de = {
  app: {
    name: 'Creator',
    loading: 'Loading …',
    retry: 'Retry',
    close: 'Close',
    cancel: 'Cancel',
    ok: 'OK',
    save: 'Save',
    back: 'Back',
    menu: 'Menu',
    offline: 'No connection – changes appear after reconnecting.',
    updateAvailable: 'A new version is available.',
    updateReload: 'Reload',
    skipToContent: 'Skip to content',
  },
  nav: {
    home: 'Home',
    navigation: 'Navigation',
    database: 'Database',
    filter: 'Filter tables',
    noMatch: 'No table found',
    favorites: 'Favourites',
    recent: 'Recently used',
    other: 'Other',
    pin: 'Pin sidebar',
    unpin: 'Hide sidebar',
    toggleSidebar: 'Toggle sidebar',
    breadcrumb: 'Breadcrumb',
    addFavorite: 'Add to favourites',
    removeFavorite: 'Remove from favourites',
  },
  user: {
    printLayouts: 'Print layouts',
    menu: 'User menu',
    language: 'Language',
    colorScheme: 'Colour scheme',
    colorTheme: 'Colour theme',
    settings: 'Settings',
    password: 'Change password',
    twoFactor: 'Two-factor sign-in',
    twoFactorOn: 'on',
    switchUser: 'Switch user',
    switchUserPrompt: 'Which user do you want to act as?',
    switchBack: 'Back to {user}',
    logout: 'Sign out',
    shortcuts: 'Keyboard shortcuts',
    impersonating: 'Signed in as {user} by {admin}.',
  },
  scheme: { auto: 'Automatic', light: 'Light', dark: 'Dark' },
  theme: {
    default: 'Default',
    blue: 'Blue',
    green: 'Green',
    grey: 'Grey',
    orange: 'Orange',
    petrol: 'Petrol',
    red: 'Red',
    sportsman: 'Sportsman',
    yellow: 'Yellow',
  },
  lang: { de: 'Deutsch', en: 'English', fr: 'Français', nl: 'Nederlands', auto: 'Browser language' },
  login: {
    title: 'Sign in',
    username: 'Username',
    password: 'Password',
    submit: 'Sign in',
    totpTitle: 'Verification code',
    totpHint: 'Enter the code from your authenticator app or a backup code.',
    totp: 'Code',
    actAs: 'Start as user (admins only)',
    invalid: 'Wrong username or password.',
    totpInvalid: 'The code is invalid.',
    throttled: 'Too many attempts. Please wait {seconds} seconds.',
    throttledShort: 'Too many attempts. Please try again later.',
    unavailable: 'Sign-in is currently unavailable.',
    expired: 'Your session has expired. Please sign in again.',
  },
  home: {
    title: 'Overview',
    databases: 'Databases',
    empty: 'No tables available.',
    tables: '{n} tables',
  },
  table: {
    rows: 'Records',
    placeholder: 'Preliminary view – the full table view follows.',
    empty: 'No records.',
    new: 'New',
    refresh: 'Refresh',
    of: 'of',
    live: 'Live updates active',
  },
  record: {
    title: 'Record {id}',
    new: 'New record',
    prev: 'Previous record',
    next: 'Next record',
    readonly: 'read only',
  },
  account: {
    title: 'Account',
    settings: 'Settings',
    password: 'Password',
    security: 'Two-factor sign-in',
    saved: 'Saved.',
    localOnly: 'Settings are currently stored on this device only.',
    sectionDisplay: 'Appearance',
    sectionTables: 'Tables and records',
    reset: 'Reset',
    resetDefault: 'Reset to default',
    showUrl: 'Open records',
    showUrlOverlay: 'As overlay above the table',
    showUrlTab: 'In a new tab',
    showUrlPage: 'As a page',
    margin: 'Cell spacing',
    marginNone: 'None',
    marginLow: 'Low',
    marginMedium: 'Medium',
    marginHigh: 'High',
    textSearch: 'Search row in tables',
    textSearchNever: 'Never show',
    textSearchAlways: 'Always show',
    textSearchFilter: 'Show only while a filter is active',
    fixedHeader: 'Fixed table header',
    sidebarPinned: 'Sidebar pinned (desktop)',
    pageSize: 'Records per page',
    pageSizeDefault: 'Table default',
    help: {
      lang: 'Language of the user interface. "Browser language" follows your browser setting.',
      colorScheme: 'Light, dark or automatic according to the device setting.',
      theme: 'Accent colours of header, sidebar and buttons.',
      margin: 'Padding inside table cells – smaller shows more rows at once.',
      sidebarPinned: 'The sidebar stays visible on large screens.',
      showUrlOverlay: 'Opens the record in a large window above the table (full screen on mobile); the table stays as it is.',
      showUrlTab: 'Opens every record in a new browser tab.',
      showUrlPage: 'Replaces the table with the record; "Back" returns to the table.',
      textSearch: 'Row of search fields below the table header. "Only while a filter is active" shows it as soon as a filter is set.',
      fixedHeader: 'The table header stays visible while scrolling.',
      pageSize: 'How many records a table loads per page.',
      shortcuts: 'Click +, then press the key combination you want (Esc cancels). Your own bindings are stored in your account.',
    },
    scope_global: 'Everywhere',
    scope_table: 'Table',
    scope_record: 'Record',
    shortcutCustom: 'customised',
    shortcutNone: 'none',
    shortcutAdd: 'Add key combination',
    shortcutRemove: 'Remove {key}',
    shortcutPress: 'Press keys …',
    shortcutRecording: 'Recording: press the new key combination, Esc cancels',
    shortcutReserved: 'This combination is reserved for the browser.',
    shortcutPlain: 'The space bar alone cannot be assigned.',
    shortcutTooMany: 'Too many key combinations for this action.',
    shortcutConflictTitle: 'Key combination already in use',
    shortcutConflict: '{key} is already used by: {actions}. Remove it there and assign it here?',
    shortcutReassign: 'Reassign',
    shortcutConflicts: 'Key combinations used more than once:',
    shortcutResetAll: 'Reset all',
    shortcutResetAllConfirm: 'Reset all keyboard shortcuts to the defaults?',
    shortcutResetDone: 'Keyboard shortcuts have been reset.',
    oldPassword: 'Current password',
    newPassword: 'New password',
    repeatPassword: 'Repeat new password',
    changePassword: 'Change password',
    showPasswords: 'Show passwords',
    passwordMismatch: 'The passwords do not match.',
    passwordChanged: 'Password changed. Other sessions have been ended.',
    passwordRules: 'At least {min} characters, not equal to the user name.',
    passwordSessionsHint: 'After the change, your other sessions (other devices, browsers) are signed out.',
    oldPasswordWrong: 'The current password is wrong.',
    passwordWeak: 'The new password does not meet the rules (at least {min} characters, not equal to the user name).',
    passwordExternal: 'The password of this account is managed externally (e.g. LDAP) and cannot be changed here.',
    pwStrength: 'Password strength',
    pwScore0: 'Too weak',
    pwScore1: 'Weak',
    pwScore2: 'Fair',
    pwScore3: 'Good',
    pwScore4: 'Very good',
    pwHint: {
      tooShort: 'at least {min} characters',
      username: 'not equal to the user name',
      tooLong: 'too long',
      mix: 'mix upper/lower case, digits and symbols',
      repeat: 'avoid repetitions and sequences',
      common: 'too common',
      longer: 'longer is safer',
      sameAsOld: 'same as the current password',
    },
    totpEnabled: 'Two-factor sign-in is enabled.',
    totpDisabled: 'Two-factor sign-in is not enabled.',
    totpIntro: 'Signing in additionally asks for a code from an authenticator app (e.g. FreeOTP, Google Authenticator, Microsoft Authenticator).',
    totpUnavailable: 'Two-factor sign-in is not configured on this server.',
    totpStart: 'Set up',
    totpStep1: 'Scan the QR code with your authenticator app or enter the key manually.',
    totpStep2: 'Enter the code shown by the app to confirm.',
    totpQr: 'QR code for the authenticator app',
    totpSecret: 'Key for the authenticator app',
    totpAccount: 'Account',
    totpManualHint: 'Time based (TOTP), 6 digits, 30 seconds.',
    totpLink: 'Open in authenticator app',
    totpVerify: 'Confirm',
    totpCode: 'Code from the app',
    totpCodeOrBackup: 'Code from the app or backup code',
    totpBackupTitle: 'Backup codes',
    totpBackup: 'Store these codes safely now – they are shown only once. Each code replaces the app code once, e.g. if you lose your phone.',
    totpBackupSaved: 'I have stored the backup codes safely.',
    copy: 'Copy',
    download: 'Download as text file',
    secretCopied: 'Key copied.',
    codesCopied: 'Backup codes copied.',
    copyFailed: 'Copying is not possible – please select the text manually.',
    done: 'Done',
    totpDisable: 'Disable',
    totpDisableHint: 'Disabling needs your password and a current code (or a backup code).',
    totpDisableConfirm: 'Really disable two-factor sign-in? Your account will then be protected by the password only.',
    totpDisableWrong: 'Password or code is wrong.',
    totpDisabledDone: 'Two-factor sign-in has been disabled.',
  },
  error: {
    generic: 'An error occurred.',
    network: 'The server cannot be reached.',
    forbidden: 'Permission denied.',
    notFound: 'Not found.',
    conflict: 'Conflict with the current state.',
    stale: 'The record has been changed by someone else in the meantime.',
    validation: 'Please check your input.',
    locked: 'The record is being edited.',
    lockedBy: 'The record is being edited by {user}.',
    rateLimited: 'Too many requests. Please wait a moment.',
    server: 'Server error. Please try again later.',
    pending: 'This view is not available yet.',
    pendingCompare: 'Comparing two records will follow in one of the next versions.',
    pendingForm: 'This legacy form has not been ported yet.',
    notFoundPage: 'This page does not exist.',
    toHome: 'Go to start page',
  },
  shortcuts: {
    title: 'Keyboard shortcuts',
    app_search: 'Search / filter tables',
    app_toggleSidebar: 'Toggle sidebar',
    app_help: 'Show keyboard shortcuts',
    table_next: 'Next row',
    table_prev: 'Previous row',
    table_open: 'Open row',
    table_new: 'New record',
    table_delete: 'Delete row',
    table_refresh: 'Refresh table',
    record_save: 'Save',
    record_saveClose: 'Save and close',
    record_close: 'Close',
    record_next: 'Next record',
    record_prev: 'Previous record',
    record_pageNext: 'Next form page',
    record_pagePrev: 'Previous form page',
    browserNote: 'Most browsers use Ctrl+Tab and Ctrl+Shift+Tab to switch tabs, so the app never sees them. Use Ctrl+Alt+→ and Ctrl+Alt+← to switch form pages instead.',
  },
  dev: { elements: 'Form elements (development)' },
};
export default en;
